import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  FileEdit,
  History,
  BarChart3,
  Zap,
  Download,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { assessmentAPI } from "../utils/api";
import { assessmentData } from "../data/assessmentData";
import { useAuth } from "../utils/AuthContext";
import "./Home.css";

const RATING_COLOR = {
  "Level A": "#16a34a",
  "Level B": "#2563eb",
  "Level B*": "#dc2626",
  "Level C": "#d97706",
  "Level D": "#dc2626",
};

export default function HomePage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    assessmentAPI
      .getAll()
      .then(({ data }) => setRecent(data.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  // Pre-calculate aggregate indicators
  const totalIndicators = assessmentData.pillars.reduce(
    (pTotal, pillar) =>
      pTotal +
      pillar.domains.reduce(
        (dTotal, domain) => dTotal + domain.indicators.length,
        0,
      ),
    0,
  );

  const totalCoreIndicators = assessmentData.pillars.reduce(
    (pTotal, pillar) =>
      pTotal +
      pillar.domains.reduce(
        (dTotal, domain) =>
          dTotal + domain.indicators.filter((i) => i.isCore).length,
        0,
      ),
    0,
  );

  return (
    <div className="home-container">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-badge">
          <Building2 className="hero-icon" />
        </div>
        <div className="hero-content">
          <span className="hero-tag">MULU HEALTH PLC</span>
          <h1>
            AHC PHC Capacity Assessment
            <span className="hero-subtitle">Scoring Workbook V5</span>
          </h1>
          <p>
            Comprehensive assessment of higher education institutions' primary
            health care capacity across Training, Research, and Community
            engagement pillars.
          </p>

          <div className="hero-meta">
            <div className="hero-meta-item">
              <span className="hero-meta-label">Max Score</span>
              <span className="hero-meta-value">1,000 pts</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-label">Pillars</span>
              <span className="hero-meta-value">3</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-label">Indicators</span>
              <span className="hero-meta-value">{totalIndicators}</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-label">CORE (×2)</span>
              <span className="hero-meta-value">{totalCoreIndicators}</span>
            </div>
          </div>

          <div className="hero-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/assessment/new")}
            >
              <FileEdit size={18} />
              Start New Assessment
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/history")}
            >
              <History size={18} />
              View History
            </button>
          </div>
        </div>
      </div>

      {/* Pillar Overview */}
      <div className="section-heading">
        <div>
          <h2 className="section-title">Assessment Pillars</h2>
          <p className="section-sub">
            Three pillars scored on a 1,000-point weighted scale
          </p>
        </div>
      </div>

      <div className="pillar-grid">
        {assessmentData.pillars.map((pillar, i) => {
          const totalInd = pillar.domains.flatMap((d) => d.indicators).length;
          const coreInd = pillar.domains
            .flatMap((d) => d.indicators)
            .filter((i) => i.isCore).length;

          return (
            <div
              key={pillar.id}
              className={`pillar-card pillar-${pillar.id}`}
              onClick={() => navigate("/assessment/new")}
            >
              <div className="pillar-header">
                <span className="pillar-card-emoji">{pillar.emoji}</span>
                <span className="pillar-badge">
                  {pillar.weightLabel} weight
                </span>
              </div>
              <h3 className="pillar-card-name">
                Pillar {i + 1}: {pillar.name}
              </h3>
              <div className="pillar-card-meta">
                <span>{totalInd} indicators</span>
                <span className="dot-divider">•</span>
                <span>{coreInd} CORE</span>
                <span className="dot-divider">•</span>
                <strong>Max {pillar.maxWeighted} pts</strong>
              </div>

              <div className="domain-list">
                {pillar.domains.map((d) => (
                  <div key={d.id} className="domain-item">
                    <span className="domain-name">
                      Domain {d.code}: {d.name}
                    </span>
                    <span className="domain-count">
                      {d.indicators.length} ind.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scoring Explainer */}
      <div className="card explainer-card">
        <div className="card-header">
          <div className="card-header-title">
            <BarChart3 size={20} className="header-icon" />
            <span className="card-title">How Scoring Works</span>
          </div>
        </div>
        <div className="card-body">
          <div className="steps-grid">
            {[
              {
                step: "1",
                title: "Enter Scores",
                desc: "Score each indicator 1–4 (Emerging → Exemplary). CORE indicators are automatically ×2 to emphasise foundational standards.",
              },
              {
                step: "2",
                title: "Ratio Scaling",
                desc: "Each pillar's raw total is ratio-scaled to its weighted ceiling: P1 ÷104 ×450 | P2 ÷64 ×300 | P3 ÷56 ×250.",
              },
              {
                step: "3",
                title: "Rating & Gatekeeper",
                desc: "Grand total determines Level D/C/B/A. Level A requires ≥801 pts AND no CORE indicator scoring ≤2 (gatekeeper rule).",
              },
            ].map((s) => (
              <div key={s.step} className="step-item">
                <div className="step-circle">{s.step}</div>
                <div className="step-content">
                  <div className="step-title">{s.title}</div>
                  <div className="step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="ratings-grid">
            {assessmentData.ratings.map((r) => (
              <div
                key={r.label}
                className="rating-card"
                style={{
                  backgroundColor: r.bg,
                  borderColor: r.color,
                }}
              >
                <div className="rating-label" style={{ color: r.color }}>
                  {r.label}
                </div>
                <div className="rating-desc">{r.description}</div>
                <div className="rating-points" style={{ color: r.color }}>
                  {r.min}–{r.max} pts
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Assessments */}
      {recent.length > 0 && (
        <div className="recent-section">
          <div className="section-heading">
            <div>
              <h2 className="section-title">Recent Assessments</h2>
            </div>
            <button className="btn-text" onClick={() => navigate("/history")}>
              View All <ArrowRight size={16} />
            </button>
          </div>
          <div className="recent-list">
            {recent.map((a) => (
              <div
                key={a._id}
                className="card recent-item"
                onClick={() => navigate(`/assessment/${a._id}`)}
              >
                <div className="recent-info">
                  <div className="recent-name">{a.institutionName}</div>
                  <div className="recent-meta">
                    {new Date(a.assessmentDate).toLocaleDateString("en-GB")} ·{" "}
                    {a.leadAssessor} · {a.generationTier}
                  </div>
                </div>

                <div className="recent-status">
                  {isAdmin ? (
                    <>
                      <div className="recent-score">
                        <div
                          className="score-value"
                          style={{ color: RATING_COLOR[a.computed?.rating] }}
                        >
                          {(a.computed?.grandTotal || 0).toFixed(0)}
                        </div>
                        <div className="score-max">/ 1,000 pts</div>
                      </div>
                      <span
                        className={`rating-badge rating-${(
                          a.computed?.rating || "Level D"
                        )
                          .replace("Level ", "")
                          .replace("*", "star")}`}
                      >
                        {a.computed?.rating || "Level D"}
                      </span>
                      <button
                        className="btn-export"
                        title="Export assessment"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(assessmentAPI.exportUrl(a._id), "_blank");
                        }}
                      >
                        <Download size={16} />
                        <span>Export</span>
                      </button>
                    </>
                  ) : (
                    <span className="rating-badge rating-hidden">
                      <ShieldCheck size={14} />{" "}
                      <span style={{ color: "red" }}>Results hidden</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* V5 Revision Notes */}
      {/* <div className="card revision-card"> */}
      {/* <div className="card-header">
        <div className="card-header-title">
          <Zap size={18} className="header-icon icon-gold" />
          <span className="card-title">V5 Revision Notes</span>
        </div> */}
      {/* <span className="card-subtitle">
          Response to Reviewer Comments — May 2026
        </span>
      </div> */}
      {/* <div className="card-body">
          <div className="changelog-grid">
            {[
              {
                issue: "HSTP-II → HSDIP-I",
                change:
                  "All references updated throughout. Indicator 2.1.2 rubric revised to reflect HSDIP-I (2025–2030).",
              },
              {
                issue: "Process vs Outcome focus",
                change:
                  "NEW indicator 3.2.6 (Community Health Impact Measurement) added. 1.2.5 and 2.2.4 rubrics outcome-strengthened.",
              },
              {
                issue: "Religious & traditional leaders",
                change:
                  "Indicator 3.2.4 expanded to explicitly include religious leaders, traditional/clan leaders, and faith-based organisations.",
              },
              {
                issue: "Scoring justification",
                change:
                  "Weight rationale (45/30/25) documented. Pillar 3 max raw increased 52→56 pts due to new 3.2.6.",
              },
            ].map((v) => (
              <div key={v.issue} className="changelog-item">
                <div className="changelog-issue">{v.issue}</div>
                <div className="changelog-change">{v.change}</div>
              </div>
            ))}
          </div>
        </div> */}
      {/* </div> */}
    </div>
  );
}
